import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { ArrowLeft, Send, Diamond, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { formatDistanceToNow } from 'date-fns';

const ProcurementDetail = () => {
  const { procurementId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  const [procurement, setProcurement] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(true);

  const [brokers, setBrokers] = useState([]);
  const [showBrokerDialog, setShowBrokerDialog] = useState(false);
  const [selectedBrokers, setSelectedBrokers] = useState([]);
  const [brokerNotes, setBrokerNotes] = useState('');

  useEffect(() => {
    if (procurementId) {
      loadProcurement();
      loadMessages();
      loadBrokers();
      const interval = setInterval(loadMessages, 3000);
      return () => clearInterval(interval);
    }
  }, [procurementId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadBrokers = async () => {
    try {
      const res = await api.get('/users/by-role/broker');
      setBrokers(res.data);
    } catch (error) {
      console.error('Error loading brokers:', error);
    }
  };

  const loadProcurement = async () => {
    try {
      const res = await api.get(`/solitaire-procurements/${procurementId}`);
      setProcurement(res.data);
    } catch (error) {
      console.error('Error loading procurement:', error);
      toast.error('Failed to load procurement');
    }
  };

  const loadMessages = async () => {
    try {
      const res = await api.get(`/solitaire-procurements/${procurementId}/messages`);
      setMessages(res.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await api.post(`/solitaire-procurements/${procurementId}/messages`, {
        content: newMessage,
        type: 'text',
      });
      setNewMessage('');
      loadMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  const handleStatusUpdate = async (newStatus, quotedAmount = null, finalAmount = null) => {
    try {
      await api.patch(`/solitaire-procurements/${procurementId}/status`, {
        status: newStatus,
        quoted_amount: quotedAmount,
        final_amount: finalAmount,
      });
      toast.success('Status updated!');
      loadProcurement();
      loadMessages();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleAssignBrokers = async () => {
    if (selectedBrokers.length === 0) {
      toast.error('Please select at least one broker');
      return;
    }

    try {
      await api.patch(`/solitaire-procurements/${procurementId}/assign-broker`, {
        broker_ids: selectedBrokers,
        notes: brokerNotes,
      });
      toast.success(`${selectedBrokers.length} broker(s) assigned!`);
      setShowBrokerDialog(false);
      setSelectedBrokers([]);
      setBrokerNotes('');
      loadProcurement();
      loadMessages();
    } catch (error) {
      console.error('Error assigning brokers:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign brokers');
    }
  };

  const toggleBrokerSelection = (brokerId) => {
    if (selectedBrokers.includes(brokerId)) {
      setSelectedBrokers(selectedBrokers.filter(id => id !== brokerId));
    } else {
      setSelectedBrokers([...selectedBrokers, brokerId]);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      requested: 'bg-gray-500',
      sourcing: 'bg-blue-500',
      quoted: 'bg-yellow-500',
      approved: 'bg-green-500',
      purchased: 'bg-purple-500',
      delivered: 'bg-emerald-500',
      cancelled: 'bg-red-500',
    };
    return colors[status] || 'bg-gray-500';
  };

  if (!procurement) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const isBroker = currentUser?.role === 'broker';
  const isAdmin = currentUser?.role === 'admin';

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-[#075E54] text-white p-3 shadow-md flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-[#128C7E]"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="font-semibold">{procurement.procurement_number}</h2>
            <p className="text-xs text-emerald-100">{procurement.title}</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-white text-xs font-medium ${getStatusColor(procurement.status)}`}>
          {procurement.status?.toUpperCase()}
        </span>
      </div>

      {/* Details Panel */}
      {showDetails && (
        <div className="bg-white border-b p-4 max-h-[40vh] overflow-y-auto">
          <div className="max-w-4xl mx-auto space-y-3">
            {/* Solitaire Specs */}
            <div className="bg-blue-50 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-blue-700 mb-2">💎 Solitaire Specifications</h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {procurement.solitaire_specs?.shape && (
                  <div><span className="text-gray-600">Shape:</span> <span className="font-medium">{procurement.solitaire_specs.shape}</span></div>
                )}
                {procurement.solitaire_specs?.size_from && (
                  <div><span className="text-gray-600">Size:</span> <span className="font-medium">{procurement.solitaire_specs.size_from}{procurement.solitaire_specs.size_to && ` - ${procurement.solitaire_specs.size_to}`} ct</span></div>
                )}
                {procurement.quantity && (
                  <div><span className="text-gray-600">Quantity:</span> <span className="font-medium">{procurement.quantity}</span></div>
                )}
                {procurement.solitaire_specs?.color && (
                  <div><span className="text-gray-600">Color Type:</span> <span className="font-medium">{procurement.solitaire_specs.color}</span></div>
                )}
                {procurement.solitaire_specs?.color_grades?.length > 0 && (
                  <div className="col-span-2"><span className="text-gray-600">Color Grades:</span> <span className="font-medium">{procurement.solitaire_specs.color_grades.join(', ')}</span></div>
                )}
                {procurement.solitaire_specs?.clarities?.length > 0 && (
                  <div className="col-span-2"><span className="text-gray-600">Clarity:</span> <span className="font-medium">{procurement.solitaire_specs.clarities.join(', ')}</span></div>
                )}
                {procurement.solitaire_specs?.cut && (
                  <div><span className="text-gray-600">Cut:</span> <span className="font-medium">{procurement.solitaire_specs.cut}</span></div>
                )}
                {procurement.solitaire_specs?.polish && (
                  <div><span className="text-gray-600">Polish:</span> <span className="font-medium">{procurement.solitaire_specs.polish}</span></div>
                )}
                {procurement.solitaire_specs?.symmetry && (
                  <div><span className="text-gray-600">Symmetry:</span> <span className="font-medium">{procurement.solitaire_specs.symmetry}</span></div>
                )}
                {procurement.solitaire_specs?.fluorescences?.length > 0 && (
                  <div className="col-span-2"><span className="text-gray-600">Fluorescence:</span> <span className="font-medium">{procurement.solitaire_specs.fluorescences.join(', ')}</span></div>
                )}
                {procurement.solitaire_specs?.labs?.length > 0 && (
                  <div className="col-span-2"><span className="text-gray-600">Labs:</span> <span className="font-medium">{procurement.solitaire_specs.labs.join(', ')}</span></div>
                )}
                {procurement.solitaire_specs?.source && (
                  <div><span className="text-gray-600">Source:</span> <span className="font-medium">{procurement.solitaire_specs.source === 'in_stock' ? 'In Stock' : 'Order'}</span></div>
                )}
                {procurement.target_date && (
                  <div><span className="text-gray-600">Target Date:</span> <span className="font-medium">{new Date(procurement.target_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</span></div>
                )}
              </div>
              {procurement.solitaire_specs?.notes && (
                <div className="mt-2 pt-2 border-t border-blue-200">
                  <span className="text-gray-600 text-xs">Notes: </span>
                  <span className="font-medium text-xs">{procurement.solitaire_specs.notes}</span>
                </div>
              )}
            </div>

            {/* Budget & Pricing */}
            <div className="grid grid-cols-2 gap-3">
              {procurement.budget && (
                <div className="bg-green-50 rounded p-2">
                  <p className="text-xs text-gray-600">Budget</p>
                  <p className="font-semibold text-green-700">₹{procurement.budget.toLocaleString('en-IN')}</p>
                </div>
              )}
              {procurement.solitaire_specs?.estimate_amount && (
                <div className="bg-blue-50 rounded p-2">
                  <p className="text-xs text-gray-600">Estimate</p>
                  <p className="font-semibold text-blue-700">₹{procurement.solitaire_specs.estimate_amount.toLocaleString('en-IN')}</p>
                </div>
              )}
              {procurement.quoted_amount && (
                <div className="bg-yellow-50 rounded p-2">
                  <p className="text-xs text-gray-600">Quoted</p>
                  <p className="font-semibold text-yellow-700">₹{procurement.quoted_amount.toLocaleString('en-IN')}</p>
                </div>
              )}
              {procurement.final_amount && (
                <div className="bg-purple-50 rounded p-2">
                  <p className="text-xs text-gray-600">Final Amount</p>
                  <p className="font-semibold text-purple-700">₹{procurement.final_amount.toLocaleString('en-IN')}</p>
                </div>
              )}
            </div>

            {/* Additional Info */}
            {procurement.notes && (
              <div className="bg-amber-50 rounded p-2">
                <p className="text-xs text-gray-600 mb-1">Procurement Notes</p>
                <p className="text-sm text-gray-900">{procurement.notes}</p>
              </div>
            )}

            {/* Broker Info */}
            {procurement.assigned_broker_names && procurement.assigned_broker_names.length > 0 && (
              <div className="bg-gray-50 rounded p-2">
                <p className="text-xs text-gray-600">Assigned Broker(s)</p>
                <p className="font-medium text-gray-900">👤 {procurement.assigned_broker_names.join(', ')}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              {/* Broker Status Updates */}
              {isBroker && procurement.status !== 'delivered' && procurement.status !== 'cancelled' && (
                <>
                  {procurement.status === 'requested' && (
                    <Button size="sm" onClick={() => handleStatusUpdate('sourcing')} className="bg-blue-500">
                      Start Sourcing
                    </Button>
                  )}
                  {procurement.status === 'sourcing' && (
                    <Button size="sm" onClick={() => {
                      const amount = prompt('Enter quoted amount (₹):');
                      if (amount) handleStatusUpdate('quoted', parseFloat(amount));
                    }} className="bg-yellow-500">
                      Submit Quote
                    </Button>
                  )}
                  {procurement.status === 'approved' && (
                    <Button size="sm" onClick={() => {
                      const amount = prompt('Enter final amount (₹):');
                      if (amount) handleStatusUpdate('purchased', null, parseFloat(amount));
                    }} className="bg-purple-500">
                      Mark Purchased
                    </Button>
                  )}
                  {procurement.status === 'purchased' && (
                    <Button size="sm" onClick={() => handleStatusUpdate('delivered')} className="bg-emerald-500">
                      Mark Delivered
                    </Button>
                  )}
                </>
              )}

              {/* Admin Actions */}
              {isAdmin && procurement.status === 'quoted' && (
                <>
                  <Button size="sm" onClick={() => handleStatusUpdate('approved')} className="bg-green-500">
                    Approve Quote
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleStatusUpdate('sourcing')}>
                    Request Re-quote
                  </Button>
                </>
              )}

              {/* Assign Broker Button */}
              {(isAdmin || currentUser?.role === 'salesman') && (
                <Button
                  size="sm"
                  onClick={() => setShowBrokerDialog(true)}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  {procurement.assigned_broker_ids?.length > 0 ? 'Reassign Brokers' : 'Assign Brokers'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toggle Button - Always Visible */}
      <div className="bg-white border-b">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="w-full text-center text-xs text-gray-500 py-2 hover:text-gray-700 hover:bg-gray-50 transition-colors"
        >
          {showDetails ? '▲ Hide Details' : '▼ Show Details'}
        </button>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto p-4 bg-[#e5ddd5]">
        <div className="max-w-4xl mx-auto space-y-2">
          {messages.map((msg) => {
            const isOwn = msg.sender_id === currentUser?.id;
            const isSystem = msg.type === 'system';

            if (isSystem) {
              return (
                <div key={msg.id} className="flex justify-center my-2">
                  <div className="bg-yellow-100 text-gray-800 px-3 py-1 rounded-lg text-xs max-w-md text-center shadow">
                    {msg.content}
                  </div>
                </div>
              );
            }

            return (
              <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-md px-3 py-2 rounded-lg shadow ${
                  isOwn ? 'bg-[#d9fdd3]' : 'bg-white'
                }`}>
                  <p className="text-xs font-semibold text-gray-700 mb-1">{msg.sender_name}</p>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDistanceToNow(new Date(msg.timestamp), { addSuffix: true })}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input */}
      <div className="bg-white border-t p-3">
        <div className="max-w-4xl mx-auto flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Type a message..."
            className="flex-1"
          />
          <Button
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            className="bg-[#25D366] hover:bg-[#20BA5A]"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Assign Broker Dialog */}
      <Dialog open={showBrokerDialog} onOpenChange={setShowBrokerDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Broker(s) to Procurement</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Select Broker(s)</Label>
              <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-3">
                {brokers.length === 0 ? (
                  <p className="text-sm text-gray-500">No brokers available. Create brokers first.</p>
                ) : (
                  brokers.map((broker) => (
                    <div
                      key={broker.id}
                      className={`p-2 border rounded cursor-pointer transition ${
                        selectedBrokers.includes(broker.id)
                          ? 'bg-blue-50 border-blue-500'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => toggleBrokerSelection(broker.id)}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedBrokers.includes(broker.id)}
                          readOnly
                          className="w-4 h-4"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{broker.username}</p>
                          <p className="text-xs text-gray-600">{broker.email}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {selectedBrokers.length > 0 && (
                <p className="text-sm text-blue-600 mt-2">
                  ✓ {selectedBrokers.length} broker(s) selected
                </p>
              )}
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <textarea
                value={brokerNotes}
                onChange={(e) => setBrokerNotes(e.target.value)}
                rows={3}
                className="w-full mt-2 px-3 py-2 border rounded-md text-sm"
                placeholder="Special instructions for brokers..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBrokerDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAssignBrokers}
              disabled={selectedBrokers.length === 0}
              className="bg-blue-500 hover:bg-blue-600"
            >
              Assign {selectedBrokers.length > 0 ? `(${selectedBrokers.length})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProcurementDetail;
